import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { MdOutlineDelete } from "react-icons/md";
import { IoReceiptOutline, IoBriefcaseOutline } from "react-icons/io5";
import { FaRegEdit } from "react-icons/fa";
import { motion } from "framer-motion";
import { IoCashOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { FaRegFolderClosed } from "react-icons/fa6";
import axios from "axios";
import { useCustomDialog } from "../../context/DialogProvider";
import { toast } from "react-toastify";
import { IoLocationOutline } from "react-icons/io5";
import EmployeeListLoading from "../../utils/EmployeeListLoading";
import { MdAlternateEmail } from "react-icons/md";
import { useUser } from "../../context/UserContext";
import { TbCalendarStats } from "react-icons/tb";
import { HiOutlineUpload } from "react-icons/hi";
import { Info } from "lucide-react";
import { LuEye } from "react-icons/lu";
import api from "../../utils/api";
import { IoSearchSharp } from "react-icons/io5";



// Add this utility function at the top
const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};


function ManageEmployees() {



  const [searchTerm, setSearchTerm] = useState('');

  const [showUploadModal, setShowUploadModal] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const employeesPerPage = 8;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { alert, confirm } = useCustomDialog();
  const fileInputRef = useRef(null);
  const baseUrl = process.env.REACT_APP_API_URL;
  const [totalPages, setTotalPages] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const { currentUser } = useUser();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [deactivateDate, setDeactivateDate] = useState("");
  const [deactivateReason, setDeactivateReason] = useState("");
  const [salaryPeriod, setSalaryPeriod] = useState("");
  const [input, setInput] = useState(0);

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  const applySearch = () => {
  navigate(`?search=${searchInput}`);
  setCurrentPage(1);
};

  const openDeactivateModal = (id) => {
    setSelectedEmpId(id);
    setDeactivateDate("");
    setDeactivateReason("");
    setShowDeactivateModal(true);
  };

  const openUploadModal = () => {
  const today = new Date();
  const currentMonth = today.toISOString().slice(0, 7); // YYYY-MM
  setSalaryPeriod(currentMonth);
  setShowUploadModal(true);
};


  // Fetch Employees function
  const fetchEmployees = async (page = 1) => {
    setIsLoading(true);
    try {
      const searchTerm = searchParams.get("search") || "";
      const response = await api.get(
        `/auth/users?page=${page - 1}&size=${employeesPerPage}&search=${searchTerm}&isActive=${true}`
      );
      const data = response.data;
      setEmployees(data.users);
      setTotalPages(data.totalPages);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch employees on page change
  useEffect(() => {
    fetchEmployees(currentPage);
  }, [currentPage]);

  // Fetch employees on search param change
  useEffect(() => {
    setCurrentPage(1);
    fetchEmployees(1);
  }, [searchParams]);

  // Delete employee handler
  const handleDeleteClick = (emp) => {
    setEmployeeToDelete(emp);
    setShowDeleteModal(true);
  };

  const navigate = useNavigate();

  // Throttled handlers
  const throttledDeleteClick = throttle(handleDeleteClick, 300);
  const throttledNavigate = throttle((path) => navigate(path), 300);

  // File change handler
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Upload function with salary period
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.warning("Please choose a file first.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    if (!salaryPeriod) {
      toast.warning("Please select a salary period.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("salaryPeriod", salaryPeriod);

    try {
      const res = await api.post(`/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Upload successful ✅", {
        position: "bottom-right",
        autoClose: 3000,
      });
      fileInputRef.current.value = "";
      setFile(null);
      setSalaryPeriod("");
      setShowUploadModal(false);
      fetchEmployees();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data || "Upload failed ❌", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeactivateSubmit = async () => {
    if (!deactivateDate || !deactivateReason) {
      toast.error("Please provide both date and reason.");
      return;
    }

    try {
      const response = await api.put(`/auth/users/${selectedEmpId}/deactivate`, {
        deactivatedDate: deactivateDate,
        reason: deactivateReason,
      });

      toast.success(response.data || "User deactivated successfully", {
        position: "bottom-right",
        autoClose: 3000,
      });

      fetchEmployees(currentPage);
      setShowDeactivateModal(false);
    } catch (error) {
      console.error("Deactivate error:", error);
      toast.error(
        error.response?.data || "Error occurred while deactivating the user.",
        { position: "bottom-right", autoClose: 3000 }
      );
    }
  };


  // Delete employee function
  const deleteEmployee = async (id) => {
    const confirmed = await confirm("Are you sure you want to delete this employee?");
    if (!confirmed) return;

    try {
      const response = await api.delete(`/deleteEmp/${id}`);

      toast.success(response.data || "Employee deleted successfully", {
        position: "bottom-right",
        autoClose: 3000,
      });

      fetchEmployees(currentPage);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data || "Failed to delete user.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const goToPage = () => {
    if (input > 0 && input <= totalPages) {
      setCurrentPage(input);
      setInput(0);
    } else {
      alert("Please enter a valid page number.");
    }
  }


   const [openView, setOpenView] = useState(false);

  // Update all navigation paths to use the dynamic baseRoute
  const handleNavigation = (path) => {
    throttledNavigate(`/hr${path}`);
  };

  const handleRowClick = (empId) => {
   navigate(`/hr/view-employee/${empId}`);



};

   const [showActions, setShowActions] = useState(false);
const [showEmailModal, setShowEmailModal] = useState(false);
const actionsRef = useRef(null);

// Close dropdown on outside click
useEffect(() => {
  const handler = (e) => {
    if (actionsRef.current && !actionsRef.current.contains(e.target)) {
      setShowActions(false);
    }
  };
  document.addEventListener("mousedown", handler);
  return () => document.removeEventListener("mousedown", handler);
}, []);

const handleSendFirstLoginEmails = async () => {
  try {
    const res = await api.post(`/auth/send-first-login-emails`);
    toast.success(res.data || "Emails sent successfully!", {
      position: "bottom-right", autoClose: 3000,
    });
    setShowEmailModal(false);
  } catch (err) {
    toast.error(err.response?.data || "Failed to send emails.", {
      position: "bottom-right", autoClose: 3000,
    });
  }
};


  return (
    <div className="overflow-hidden p-2 xs:p-3 sm:p-4">

      {/* Header */}
<motion.div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-1 mb-4 bg-white rounded-2xl p-4">
  
  {/* Left: Title */}
  <div>
    <h2 className="text-xl font-semibold text-slate-800">Active employees</h2>
    <p className="text-sm text-slate-400">{employees.length} employees shown</p>
  </div>

  <div className="flex items-center gap-3">
    {/* Search */}
<div className="relative">
  <input
    type="text"
    placeholder="Search employee..."
    value={searchInput}
    onChange={(e) => setSearchInput(e.target.value)}   // only updates local state
    onKeyDown={(e) => {
      if (e.key === 'Enter') applySearch();             // apply on Enter
    }}
    className="pl-9 pr-4 py-2 text-sm rounded-full border border-slate-200 bg-slate-50 focus:outline-none focus:border-slate-400 w-56"
  />
  <IoSearchSharp
    onClick={applySearch}                               // apply on icon click
    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm cursor-pointer hover:text-slate-600"
  />
</div>

    {/* Actions Dropdown */}
    <div className="relative" ref={actionsRef}>
      <button
        onClick={() => setShowActions((prev) => !prev)}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
      >
        Actions
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${showActions ? "rotate-180" : ""}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        >
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>

      {showActions && (
        <div className="absolute right-0 top-[calc(100%+6px)] bg-white border border-slate-200 rounded-xl shadow-lg p-1.5 w-52 z-50">
          
          {/* Upload Salary Slip */}
          <button
            onClick={() => { setShowActions(false); openUploadModal(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg text-left"
          >
            <HiOutlineUpload className="text-slate-400 text-base" />
            Upload salary slip
          </button>

          {/* Add New Employee */}
          <button
            onClick={() => { setShowActions(false); handleNavigation("/AddEmployee"); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg text-left"
          >
            <svg className="w-4 h-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="19" y1="8" x2="19" y2="14"/>
              <line x1="22" y1="11" x2="16" y2="11"/>
            </svg>
            On boarding employee
          </button>

          <div className="my-1 border-t border-slate-100" />

          {/* Send Email */}
          <button
            onClick={() => { setShowActions(false); setShowEmailModal(true); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg text-left"
          >
            <svg className="w-4 h-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
            </svg>
            Send email
          </button>
        </div>
      )}
    </div>
  </div>
</motion.div>

{/* Upload Modal — keep your existing showUploadModal JSX here, unchanged */}
{showUploadModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 mx-4">

      {/* Header */}
      <div className="flex justify-between items-start mb-5">
        <div>
          <h2 className="text-base font-semibold text-slate-800">
            Upload Salary Slip
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Upload employee salary file
          </p>
        </div>

        <button
          onClick={() => setShowUploadModal(false)}
          className="text-slate-400 hover:text-slate-600"
        >
          ✕
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleUpload} className="space-y-4">

        {/* Salary Period */}
        <div>
          <label className="text-sm text-slate-600">Salary Period</label>
          <input
            type="month"
            value={salaryPeriod}
            onChange={(e) => setSalaryPeriod(e.target.value)}
            className="w-full mt-1 border p-2 rounded-lg"
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="text-sm text-slate-600">Upload File</label>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="w-full mt-1"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 pt-3">
          <button
            type="button"
            onClick={() => setShowUploadModal(false)}
            className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isUploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
          >
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}


{/* Send Email Modal */}
{showEmailModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 mx-4">
      
      <div className="flex justify-between items-start mb-5">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Send email</h2>
          <p className="text-sm text-slate-400 mt-0.5">Trigger email notifications to employees</p>
        </div>
        <button onClick={() => setShowEmailModal(false)} className="text-slate-400 hover:text-slate-600">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Option card */}
      <button
        onClick={handleSendFirstLoginEmails}
        className="w-full flex items-start gap-4 p-4 rounded-xl border border-slate-200 hover:border-slate-400 text-left transition-colors group mb-5"
      >
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="4" width="20" height="16" rx="2"/>
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
          </svg>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-800">First-time login emails</p>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">
            Send account credentials and login instructions to all employees who haven't logged in yet.
          </p>
        </div>
      </button>

      <div className="flex justify-end gap-2">
        <button
          onClick={() => setShowEmailModal(false)}
          className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

      {/* Table Section - Only visible on larger screens */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        transition={{ duration: 0.2 }}
      >
        <div className="overflow-x-auto">
          {isLoading ? (
            <EmployeeListLoading/>
          ) : (
            <table className="w-full">
              <thead className="bg-white border-b border-slate-200">
                <tr>
                  <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">S.No</th>
                  <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee ID</th>
                  <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Employee Name</th>
                  <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Department</th>
                  <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Location</th>
                  <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">Email</th>
                  <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employees && employees.length > 0 ? (
                  employees.map((emp, index) => (
                    <tr
  key={emp.id || index}
  onClick={() => handleRowClick(emp.id)}
  className="hover:bg-slate-50/50 transition-colors duration-150 cursor-pointer"
  title="Click to view employee details"
>

                      <td className="px-3 py-2 text-slate-600">
                        {((currentPage - 1) * employeesPerPage) + index + 1}
                      </td>
                       <td className="px-3 py-2 text-sm text-slate-600">{emp.employeeId}</td>
                      <td className="px-3 py-2 text-sm text-slate-700 ">{emp.name}</td>
                      <td className="px-3 py-2 text-sm text-slate-600">{emp.department || "—"}</td>
                      <td className="px-3 py-2 text-sm text-slate-600">{emp.location}</td>
                      <td className="px-3 py-2 text-sm text-slate-600">{emp.email}</td>
                     
                      <td className="px-3 py-2 text-sm"
                         onClick={(e) => e.stopPropagation()}
                      >

       
                        <div className="flex justify-center gap-3">
                          {/* <motion.button
                            onClick={() => handleNavigation(`/view-employee/${emp.id}`)}
                            className="text-indigo-600 hover:text-indigo-800 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="View"
                          >
                            <LuEye className="text-xl" />
                          </motion.button> */}

                          <motion.button
                            onClick={(e) =>{
                            e.stopPropagation();
                            handleNavigation(`/employees/EditEmployee/${emp.id}`)}} 
                            className="text-orange-600 hover:text-orange-800 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Edit"
                          >
                            <FaRegEdit className="text-xl" />
                          </motion.button>
                          
                          {/* <motion.button
                            onClick={() => handleNavigation(`/employees/leavebalance/${emp.id}`)}
                            className="text-purple-600 hover:text-purple-800 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Leave Balance"
                          >
                            <TbCalendarStats className="text-xl" />
                          </motion.button> */}
                          
                          {/* <motion.button
                            onClick={() => handleNavigation(`/employees/salary/${emp.id}`)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Salary Details"
                          >
                            <IoCashOutline className="text-xl" />
                          </motion.button> */}
                          
                          <motion.button
                            onClick={() => handleNavigation(`/employees/payslips/${emp.id}`)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                    
                            title="View Payslips"
                          >
                            <IoReceiptOutline className="text-xl" />
                          </motion.button>
                          
                          <motion.button
                            onClick={() => openDeactivateModal(emp.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Deactivate Employee"
                          >
                            <MdOutlineDelete className="text-xl" />
                          </motion.button>
                        </div>
                        
                      </td>
                      
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-slate-400">
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Section */}
        <div className="px-2 py-2 border-t border-slate-100 flex justify-between items-center">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="small"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#ffffff",
                },
              }}
            />
          </Stack>

          <div className="flex gap-2 place-items-start mr-12">
            <input
              type="number"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="border border-gray-300 rounded-md w-12 px-1 py-1 text-center"
            />
            <button 
              className="bg-slate-700 text-white px-2 py-1 rounded-md"
              onClick={goToPage}
            >
              Go
            </button>
          </div>
        </div>
      </motion.div>

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-4">Deactivate Employee</h2>

            <label className="block mb-2 text-sm">Deactivation Date</label>
            <input
              type="date"
              value={deactivateDate}
              onChange={(e) => setDeactivateDate(e.target.value)}
              className="w-full border p-2 rounded-lg mb-4"
            />

            <label className="block mb-2 text-sm">Reason</label>
            <textarea
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              className="w-full border p-2 rounded-lg mb-4"
              rows="3"
            ></textarea>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeactivateModal(false)}
                className="px-4 py-2 bg-gray-300 rounded-full hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateSubmit}
                className="px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <motion.div
        className="sm:hidden mt-4 pb-14"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="grid grid-cols-1 gap-4">
          {employees.length > 0 ? (
            employees.map((emp, index) => (
              <motion.div
                key={index}
                className="bg-white p-5 rounded-xl shadow-md border border-slate-100"
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.2 }}
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800 text-lg">{emp.name}</h3>
                    <span className="text-sm font-medium text-slate-500">{emp.employeeId}</span>
                  </div>
                  <div className="space-y-2">
                    {emp.department && (
                      <p className="text-base text-slate-600 flex items-center gap-3">
                        <span className="w-5 h-5 text-slate-400"><IoBriefcaseOutline /></span>
                        {emp.department}
                      </p>
                    )}
                    <p className="text-base text-slate-600 flex items-center gap-3">
                      <span className="w-5 h-5 text-slate-400"><IoLocationOutline /></span>
                      {emp.location}
                    </p>
                    <p className="text-base text-slate-600 flex items-center gap-3">
                      <span className="w-5 h-5 text-slate-400"><MdAlternateEmail /></span>
                      {emp.email}
                    </p>
                  </div>
                  <div className="pt-4 mt-4 border-t border-slate-100">
                    <div className="flex justify-around items-center">
                      <motion.button
                        onClick={() => handleNavigation(`/employees/leavebalance/${emp.id}`)}
                        className="p-3 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <TbCalendarStats className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleNavigation(`/employees/salary/${emp.id}`)}
                        className="p-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <IoCashOutline className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleNavigation(`/employees/payslips/${emp.id}`)}
                        className="p-3 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <IoReceiptOutline className="text-xl" />
                      </motion.button>
                      <motion.button
                        onClick={() => throttledDeleteClick(emp)}
                        className="p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <MdOutlineDelete className="text-xl"/>
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-400 bg-white rounded-xl">
              No employees found
            </div>
          )}
        </div>

        {/* Mobile Pagination */}
        <div className="mt-6 flex justify-center">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#ffffff",
                },
              }}
            />
          </Stack>
        </div>
      </motion.div>
    </div>
  );
}

export default ManageEmployees;